import { useRef, type KeyboardEvent } from 'react';

/**
 * A row of a few always-visible options, exactly one selected — for switching
 * how a screen shows its content. The screen owns the value; this only
 * reports the next one. One tab stop: arrows move the selection, like native
 * radio buttons.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  /** The group's accessible name. */
  label: string;
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const current = options.findIndex((option) => option.value === value);

  function step(event: KeyboardEvent, index: number) {
    const by =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (by === 0) return;
    event.preventDefault();
    const next = (index + by + options.length) % options.length;
    onChange(options[next].value);
    buttons.current[next]?.focus();
  }

  return (
    <div role="radiogroup" aria-label={label} className="segmented-control">
      {options.map((option, index) => {
        const on = index === current;
        return (
          <button
            key={option.value}
            ref={(el) => {
              buttons.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={on}
            // Nothing selected (a stale value) still leaves one way in.
            tabIndex={on || (current === -1 && index === 0) ? 0 : -1}
            className={on ? 'segmented-option is-active' : 'segmented-option'}
            onClick={() => {
              if (!on) onChange(option.value);
            }}
            onKeyDown={(e) => step(e, index)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
